import React, { useState, useEffect } from 'react';
import axios from '../../../../api/myAxios';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemButton,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemText,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createWorkspaceSchema } from '../../../../schemas/workspaceSchema';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkspaceFormData, Workspace } from '../../../../types';
import { AxiosError } from 'axios';

const WorkspaceList = ({
  onSelect,
  activeWorkspace,
  setActiveWorkspace,
}: {
  onSelect: (workspace: Workspace | null) => void;
  activeWorkspace: number;
  setActiveWorkspace: (workspace: number) => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<string | null>(
    null
  );
  const [errorDeletingWorkspace, setErrorDeletingWorkspace] = useState('');
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(
    null
  );
  const theme = useTheme();
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const {
    data: workspaces = [] as Workspace[],
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => axios.get('/api/workspaces').then((res) => res.data),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm<WorkspaceFormData>({
    resolver: yupResolver(createWorkspaceSchema(t)),
  });

  const createWorkspaceMutation = useMutation({
    mutationFn: async (data: WorkspaceFormData) => {
      const response = await axios.post('/api/workspaces', {
        name: data.newWorkspaceName,
        description: data.description,
        heroBannerData: {
          title: t('personalizedPage.welcomeTitle', {
            workspaceName: data.newWorkspaceName,
          }),
          description: t('personalizedPage.assistantDescription'),
          tags: [],
          selectedBackground: 'default',
          bannerTextColor: 'white',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setSuccess(t('workspace.createWorkspaceSuccess'));
      setShowForm(false);
      reset();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      if (error.response?.data?.message) {
        setError('newWorkspaceName', {
          type: 'manual',
          message: error.response.data.message,
        });
      } else {
        setError('newWorkspaceName', {
          type: 'manual',
          message: t('workspace.createWorkspaceError'),
        });
      }
    },
  });

  const deleteWorkspaceMutation = useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`/api/workspaces/${id}`);
    },
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ['workspaces'] });
      setSuccess(t('workspace.deleteSuccess'));
      if (selectedWorkspace && selectedWorkspace.id === deletedId) {
        setSelectedWorkspace(null);
        setActiveWorkspace(-1);
        onSelect(null);
      }
    },
    onError: () => {
      setErrorDeletingWorkspace(t('workspace.deleteErrorMessage'));
    },
  });

  const handleCreateWorkspace = (data: WorkspaceFormData) => {
    createWorkspaceMutation.mutate(data);
  };

  const handleDeleteWorkspace = (id: string) => {
    deleteWorkspaceMutation.mutate(id);
  };

  const handleSelectWorkspace = (workspace: Workspace, index: number) => {
    setSelectedWorkspace(workspace);
    setActiveWorkspace(index);
    onSelect(workspace);
  };

  const handleDeleteClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setWorkspaceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (workspaceToDelete) {
      handleDeleteWorkspace(workspaceToDelete);
      setDeleteDialogOpen(false);
      setWorkspaceToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ mb: 3, p: 4, textAlign: 'center' }}>
        <Typography>Chargement...</Typography>
      </Paper>
    );
  }

  if (fetchError) {
    return (
      <Paper sx={{ mb: 3 }}>
        <Alert severity="error">{t('workspace.fetchingErrorMessage')}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mb: 3 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">{t('workspace.title')}</Typography>
        <Button
          variant="contained"
          size="small"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowForm(true)}
        >
          {t('workspace.newWorkspace')}
        </Button>
      </Box>
      <Divider />
      {success && (
        <Fade in={success !== ''} timeout={1000}>
          <Alert severity="success" sx={{ width: '100%', mb: 3 }}>
            {success}
          </Alert>
        </Fade>
      )}
      {showForm && (
        <Box sx={{ p: 2, width: '100%' }}>
          <form
            onSubmit={handleSubmit((data) => {
              handleCreateWorkspace(data);
            })}
            noValidate
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <TextField
              type="text"
              variant="outlined"
              margin="normal"
              label={t('workspace.name')}
              error={!!errors.newWorkspaceName?.message}
              helperText={errors.newWorkspaceName?.message}
              {...register('newWorkspaceName', {
                required: t('workspace.nameRequired'),
              })}
            />
            <TextField
              type="text"
              variant="outlined"
              margin="normal"
              label={t('workspace.description')}
              multiline
              rows={3}
              error={!!errors.description?.message}
              helperText={errors.description?.message}
              {...register('description', {
                required: t('workspace.descriptionRequired'),
              })}
            />
            <Button
              type="submit"
              size="small"
              variant="contained"
              color="primary"
              fullWidth
            >
              {t('workspace.createWorkspace')}
            </Button>
          </form>
        </Box>
      )}
      {workspaces.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('workspace.noWorkspaces')}
          </Typography>
          <Typography variant="body2">
            {t('workspace.noWorkspacesDescription')}
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0, overflowY: 'auto', maxHeight: '500px' }}>
          {errorDeletingWorkspace && (
            <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
              {errorDeletingWorkspace}
            </Alert>
          )}
          {workspaces.map((workspace: Workspace, index: number) => (
            <ListItem key={workspace.id} disablePadding>
              <ListItemButton
                selected={index === activeWorkspace}
                onClick={() => handleSelectWorkspace(workspace, index)}
                sx={{
                  borderLeft:
                    index === activeWorkspace
                      ? `4px solid ${theme.palette.primary.main}`
                      : '4px solid transparent',
                  backgroundColor:
                    index === activeWorkspace
                      ? theme.palette.action.selected
                      : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      index === activeWorkspace
                        ? theme.palette.action.selected
                        : theme.palette.action.hover,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: index === activeWorkspace ? 600 : 400,
                            color:
                              index === activeWorkspace
                                ? theme.palette.primary.main
                                : 'inherit',
                          }}
                        >
                          {workspace.name}
                        </Typography>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          size="small"
                          onClick={(e) => handleDeleteClick(e, workspace.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    }
                    secondary={workspace.description}
                  />
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('workspace.confirmation')}</DialogTitle>
        <DialogContent>{t('workspace.deleteConfirmation')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('workspace.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error">
            {t('workspace.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default WorkspaceList;
